import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Pencil, Plus, Search, Settings } from 'lucide-react';
import { IconBuildingFactory2, IconEye, IconGauge, IconLayersSubtract, IconSearch, IconX } from '@tabler/icons-react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import '@fontsource-variable/geist/wght.css';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import { BoardClickUpFilterChipDropdown } from '../../board/toolbar/BoardClickUpFilterChipDropdown';
import { GoHoverTooltip } from './GoHoverTooltip';
import {
  createGoMeter,
  fetchGoMeterEquipmentCatalog,
  fetchGoMeters,
  putGoMeterGrpLinks,
  putGoMeterTechLinks,
  searchGoMeterCollectionsGroups,
  updateGoMeter,
} from '../services/goMeterApi';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import '../../process-config/ConfigStatusView2.css';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../cadastros/compatibilidade-equipamentos/CompatibilidadeEquipamentosPage.css';
import './GoHoverTooltip.css';
import './GoMedicoesConfig.css';

const GRUPOS_SEARCH_MIN = 2;

const VISAO_TO_STATUS = { Vinculados: 'vinculados', Livres: 'livres' };
const STATUS_TO_VISAO = { vinculados: 'Vinculados', livres: 'Livres' };
const AGRUP_TO_KEY = { Tecnologia: 'tec', Fabricante: 'fab' };
const KEY_TO_AGRUP = { tec: 'Tecnologia', fab: 'Fabricante' };

function NovaFormaChipIcon({ size = 13, stroke = 1.5, strokeWidth, ...rest }) {
  return <Plus size={size} strokeWidth={strokeWidth ?? stroke} {...rest} />;
}

function Pcs2Section({ icon: Icon, title, desc, children, className = '', headingExtra = null }) {
  return (
    <section className={['pcs2-section', className].filter(Boolean).join(' ')}>
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} stroke={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
          {headingExtra}
        </div>
        {desc ? <p className="pcs2-section-desc">{desc}</p> : null}
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function GoMedListSettings({ showInactive, onChange }) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="go-med-gear-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`pcs2-btn-gear${open || showInactive ? ' is-on' : ''}`}
        aria-label="Opções da lista de medições"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Settings size={15} strokeWidth={1.75} aria-hidden />
      </button>
      {open ? (
        <div className="go-med-gear-menu" role="menu">
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={showInactive}
            className={`go-med-gear-item${showInactive ? ' is-on' : ''}`}
            onClick={() => onChange(!showInactive)}
          >
            Exibir medições inativas
          </button>
        </div>
      ) : null}
    </div>
  );
}

const TEC_COR = {
  'A4 P&B': '#64748b',
  'A4 Colorido': '#0ea5a4',
  'A3 P&B': '#7c6f9c',
  'A3 Colorido': '#e07a3f',
  Scanner: '#4f8cd9',
};

function techColor(name) {
  const key = String(name || '').trim();
  if (TEC_COR[key]) return TEC_COR[key];
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = key.charCodeAt(i) + ((h << 5) - h);
  return `hsl(${Math.abs(h) % 360} 42% 48%)`;
}

function countEquipForLinks(codes) {
  return (codes || []).length;
}

function codesToItems(codes, catalog) {
  const byCode = new Map(catalog.map((m) => [String(m.equipmentCode), m]));
  return (codes || [])
    .map((code) => {
      const key = String(code || '').trim();
      const m = byCode.get(key);
      if (!m) return null;
      return {
        equipmentCode: m.equipmentCode,
        idBrand: m.idBrand,
        idSubgroup: m.idSubgroup,
      };
    })
    .filter(Boolean);
}

const ERP_OPS = [
  'A4 preto e branco',
  'A4 colorido',
  'Digitalização',
  'A3 preto e branco',
  'A3 colorido',
  'Quantidade de serviço',
];

function mapGrpInfo(row) {
  if (!row) return null;
  const idCollectionsGroup = String(row.idCollectionsGroup || row.id || '').trim();
  if (!idCollectionsGroup) return null;
  const idContract = String(row.idContract || '').trim();
  const clientName = String(row.clientName || '').trim();
  const nameCollectionsGroup = String(row.nameCollectionsGroup || row.nome || '').trim();
  const equipmentCodes = Array.isArray(row.equipmentCodes)
    ? [...new Set(row.equipmentCodes.map((c) => String(c || '').trim()).filter(Boolean))]
    : [];
  return {
    idCollectionsGroup,
    idContract,
    clientName,
    nameCollectionsGroup,
    equipmentCodes,
    contractLabel:
      String(row.contractLabel || '').trim() ||
      [idContract, clientName].filter(Boolean).join(' · ') ||
      idCollectionsGroup,
  };
}

function gruposToLinkItems(groupIds, infoList) {
  const byId = new Map(
    (infoList || []).map((g) => {
      const info = mapGrpInfo(g);
      return info ? [info.idCollectionsGroup, info] : null;
    }).filter(Boolean)
  );
  const items = [];
  (groupIds || []).forEach((gid) => {
    const info = byId.get(String(gid));
    const codes = info?.equipmentCodes || [];
    codes.forEach((equipmentCode) => {
      items.push({
        idCollectionsGroup: String(gid),
        equipmentCode,
      });
    });
  });
  return items;
}

/** Mapeia registro da API para o shape da UI. */
function mapMeterToForma(row, prev) {
  const fromApiModelos = Array.isArray(row.modelos) ? row.modelos.map(String) : null;
  const fromApiGrupos = Array.isArray(row.grupos) ? row.grupos.map(String) : null;
  const fromApiInfo = Array.isArray(row.gruposInfo)
    ? row.gruposInfo.map(mapGrpInfo).filter(Boolean)
    : null;
  return {
    id: Number(row.id),
    nome: String(row.nome || '').trim(),
    unit: String(row.unit || '').trim().slice(0, 3),
    medidor: Boolean(row.requiresPrevMeter),
    erp: Boolean(row.erpEnabled),
    erpOps: Array.isArray(row.erpOps) ? row.erpOps.slice(0, 1) : [],
    isActive: row.isActive !== false,
    modelos: fromApiModelos || (prev?.modelos ? [...prev.modelos] : []),
    grupos: fromApiGrupos || (prev?.grupos ? [...prev.grupos] : []),
    gruposInfo: fromApiInfo || (prev?.gruposInfo ? [...prev.gruposInfo] : []),
  };
}

function groupBy(items, keyFn) {
  const map = new Map();
  items.forEach((it) => {
    const k = keyFn(it);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(it);
  });
  return [...map.entries()].map(([label, list]) => ({ label, list }));
}

function MedicoesModal({ draft, onClose, onSave, onChange, submitting }) {
  if (!draft) return null;

  return createPortal(
    <div className="go-med-modal-overlay" role="presentation">
      <div
        className="go-med-modal"
        role="dialog"
        aria-modal="true"
        aria-label={draft.id ? 'Editar tipo de medição' : 'Novo tipo de medição'}
      >
        <header className="go-med-modal-head">
          <h3>{draft.id ? 'Editar tipo de medição' : 'Novo tipo de medição'}</h3>
          <button
            type="button"
            className="go-med-icon-btn"
            onClick={onClose}
            aria-label="Fechar"
            disabled={submitting}
          >
            <IconX size={14} stroke={1.75} />
          </button>
        </header>
        <div className="go-med-modal-body">
          <div className="go-med-field-row">
            <label className="go-med-field grow">
              <span>Nome</span>
              <input
                type="text"
                value={draft.nome}
                onChange={(e) => onChange({ ...draft, nome: e.target.value })}
                placeholder="Ex.: Cópia Mono a4 ou Impressão Mono a4"
                maxLength={120}
                autoFocus
                disabled={submitting}
              />
            </label>
            <label className="go-med-field go-med-field-unit">
              <span>Unidade</span>
              <input
                type="text"
                value={draft.unit || ''}
                onChange={(e) =>
                  onChange({ ...draft, unit: String(e.target.value || '').slice(0, 3) })
                }
                placeholder="un"
                maxLength={3}
                disabled={submitting}
                aria-label="Unidade"
              />
            </label>
          </div>
          <button
            type="button"
            className={`go-med-toggle-card${draft.medidor ? ' is-on' : ''}`}
            onClick={() => onChange({ ...draft, medidor: !draft.medidor })}
            disabled={submitting}
          >
            <span className="go-med-switch" aria-hidden>
              <span />
            </span>
            <span>
              <strong>Necessita de medidor anterior em novo lançamento</strong>
              <small>Exige a leitura anterior do equipamento ao lançar uma nova medição.</small>
            </span>
          </button>
          <button
            type="button"
            className={`go-med-toggle-card${draft.erp ? ' is-on' : ''}`}
            onClick={() =>
              onChange({
                ...draft,
                erp: !draft.erp,
                erpOps: !draft.erp ? draft.erpOps || [] : [],
              })
            }
            disabled={submitting}
          >
            <span className="go-med-switch" aria-hidden>
              <span />
            </span>
            <span>
              <strong>Habilitar integração com ERP externo</strong>
              <small>Define quais contadores serão enviados ao ERP.</small>
            </span>
          </button>
          {draft.erp ? (
            <div className="go-med-erp-box">
              <div className="go-med-erp-label">
                Como integra com o ERP
                {(draft.erpOps || []).length === 0 ? (
                  <em>Selecione um contador</em>
                ) : null}
              </div>
              <div className="go-med-erp-chips" role="radiogroup" aria-label="Contador ERP">
                {ERP_OPS.map((op) => {
                  const on = (draft.erpOps || []).includes(op);
                  return (
                    <button
                      key={op}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      className={`go-med-erp-chip${on ? ' is-on' : ''}`}
                      disabled={submitting}
                      onClick={() => {
                        onChange({
                          ...draft,
                          erpOps: on ? [] : [op],
                        });
                      }}
                    >
                      <span className={`go-med-erp-mark${on ? ' is-on' : ''}`} aria-hidden />
                      {op}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
        <footer className="go-med-modal-foot">
          {draft.id ? (
            <button
              type="button"
              className={`go-med-inactivate${draft.isActive === false ? ' is-on' : ''}`}
              onClick={() => onChange({ ...draft, isActive: draft.isActive === false })}
              disabled={submitting}
              aria-pressed={draft.isActive === false}
            >
              <span className="go-med-switch" aria-hidden>
                <span />
              </span>
              Inativar
            </button>
          ) : (
            <span />
          )}
          <div className="go-med-modal-foot-actions">
            <button
              type="button"
              className="user-form-link-btn user-form-footer-btn"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="user-form-profile-save-btn user-form-footer-btn"
              disabled={submitting || !String(draft.nome || '').trim()}
              onClick={() => onSave(draft)}
            >
              {submitting ? 'Salvando…' : draft.id ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
}

function BuscaModeloModal({ open, models, onClose }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setQ('');
    setPicked(null);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return models;
    return models.filter((m) => m.busca.includes(s));
  }, [models, q]);

  const selected = models.find((m) => m.equipmentCode === picked) || null;

  if (!open) return null;

  return createPortal(
    <div className="go-med-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className={`go-med-modal go-med-modelo-modal${selected ? ' is-detail' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Buscar medições por modelo"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="go-med-modal-head">
          <h3>Buscar medições por modelo</h3>
          <button type="button" className="go-med-icon-btn" onClick={onClose} aria-label="Fechar">
            <IconX size={14} stroke={1.75} />
          </button>
        </header>
        <div className="go-med-modelo-body">
        <div className="go-med-modelo-search">
          <IconSearch size={14} stroke={1.75} aria-hidden />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Digite o modelo…"
            aria-label="Pesquisar modelo"
          />
        </div>
        <div className="board-search-filters-popover board-search-filters-popover--simple ce-compat-eq-popover go-med-modelo-results">
          <div className="bsf-simple-picker-list" role="listbox" aria-label="Modelos com medições">
            {hits.length === 0 ? (
              <div className="bsf-simple-picker-empty">
                {models.length === 0
                  ? 'Nenhum modelo com medição vinculada.'
                  : 'Nenhum modelo encontrado.'}
              </div>
            ) : (
              hits.map((m) => {
                const on = m.equipmentCode === picked;
                return (
                  <button
                    key={m.equipmentCode}
                    type="button"
                    role="option"
                    aria-selected={on}
                    className={`bsf-simple-picker-item${on ? ' is-selected' : ''}`}
                    onClick={() => setPicked(m.equipmentCode)}
                  >
                    <span className="bsf-simple-picker-item-label">{m.equipment}</span>
                    <span className="bsf-simple-picker-item-meta-slot">
                      <span className="bsf-simple-picker-item-meta ce-compat-eq-meta ce-compat-eq-meta--links">
                        {m.medicoes.length}
                      </span>
                    </span>
                    <Check size={12} strokeWidth={2.5} className="bsf-simple-picker-check" aria-hidden />
                  </button>
                );
              })
            )}
          </div>
        </div>
        {selected ? (
          <div className="go-med-modelo-detail">
            <p className="go-med-modelo-detail-title">
              Medições vinculadas
              <span>{selected.medicoes.length}</span>
            </p>
            <ul className="go-med-modelo-links">
              {selected.medicoes.map((f) => (
                <li key={f.id}>
                  <span className="go-med-modelo-link-nome">{f.nome}</span>
                  <span className="go-med-modelo-link-tags">
                    {f.unit ? <span>{String(f.unit).toLowerCase()}</span> : null}
                    {f.medidor ? <span>medidor anterior</span> : null}
                    {f.erp ? <span>erp</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function GoMedicoesConfig() {
  const [formas, setFormas] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [gruposHits, setGruposHits] = useState([]);
  const [gruposSearching, setGruposSearching] = useState(false);
  const [gruposSearchError, setGruposSearchError] = useState(null);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [savingModal, setSavingModal] = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);
  const [buscaForma, setBuscaForma] = useState('');
  const [buscaFormaOpen, setBuscaFormaOpen] = useState(false);
  const buscaFormaInputRef = useRef(null);
  const [tab, setTab] = useState('modelos');
  const [busca, setBusca] = useState('');
  const [fabricante, setFabricante] = useState('todos');
  const [status, setStatus] = useState('todos');
  const [agrup, setAgrup] = useState('tec');
  const [abertos, setAbertos] = useState({});
  const [modal, setModal] = useState(null);
  const [fabFilterOpen, setFabFilterOpen] = useState(false);
  const [visaoFilterOpen, setVisaoFilterOpen] = useState(false);
  const [agrupFilterOpen, setAgrupFilterOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [modeloBuscaOpen, setModeloBuscaOpen] = useState(false);
  const closeModeloBusca = useCallback(() => setModeloBuscaOpen(false), []);
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();

  const loadFormas = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [rows, equip] = await Promise.all([
        fetchGoMeters({ includeInactive: true }),
        fetchGoMeterEquipmentCatalog().catch(() => []),
      ]);
      setCatalog(Array.isArray(equip) ? equip : []);
      setFormas((prev) => {
        const prevMap = new Map(prev.map((f) => [f.id, f]));
        return rows.map((row) => mapMeterToForma(row, prevMap.get(Number(row.id))));
      });
      setSel((curr) => {
        if (curr != null && rows.some((r) => Number(r.id) === Number(curr))) return Number(curr);
        return rows[0] ? Number(rows[0].id) : null;
      });
    } catch (err) {
      setLoadError(err.message || 'Erro ao carregar tipos de medição');
      setFormas([]);
      setSel(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFormas();
  }, [loadFormas]);

  useEffect(() => {
    if (!buscaFormaOpen) return undefined;
    const t = window.setTimeout(() => buscaFormaInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [buscaFormaOpen]);

  useEffect(() => {
    if (tab !== 'grupos') return undefined;
    const term = busca.trim();
    if (term.length < GRUPOS_SEARCH_MIN) {
      setGruposHits([]);
      setGruposSearchError(null);
      setGruposSearching(false);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setGruposSearching(true);
      setGruposSearchError(null);
      try {
        const rows = await searchGoMeterCollectionsGroups(term);
        if (!cancelled) setGruposHits(Array.isArray(rows) ? rows.map(mapGrpInfo).filter(Boolean) : []);
      } catch (err) {
        if (!cancelled) {
          setGruposHits([]);
          setGruposSearchError(err.message || 'Erro ao buscar grupos');
        }
      } finally {
        if (!cancelled) setGruposSearching(false);
      }
    }, 320);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [tab, busca]);

  const forma = formas.find((f) => f.id === sel) || null;
  const isModelos = tab === 'modelos';
  const campo = isModelos ? 'modelos' : 'grupos';
  const gruposBuscaAtiva = !isModelos && busca.trim().length >= GRUPOS_SEARCH_MIN;

  const fabricantes = useMemo(
    () => [...new Set(catalog.map((m) => m.brandNome).filter(Boolean))].sort(),
    [catalog]
  );

  const fabSelected = useMemo(
    () => (fabricante && fabricante !== 'todos' ? [fabricante] : []),
    [fabricante]
  );

  const visaoSelected = useMemo(
    () => (status === 'todos' ? [] : [STATUS_TO_VISAO[status]].filter(Boolean)),
    [status]
  );

  const agrupSelected = useMemo(() => [KEY_TO_AGRUP[agrup] || 'Tecnologia'], [agrup]);

  const closeOtherFilters = (keep) => {
    if (keep !== 'fab') setFabFilterOpen(false);
    if (keep !== 'visao') setVisaoFilterOpen(false);
    if (keep !== 'agrup') setAgrupFilterOpen(false);
  };

  const modelosComMedicao = useMemo(() => {
    const byCode = new Map();
    formas.forEach((f) => {
      if (!showInactive && f.isActive === false) return;
      (f.modelos || []).forEach((code) => {
        const key = String(code || '').trim();
        if (!key) return;
        if (!byCode.has(key)) byCode.set(key, []);
        byCode.get(key).push(f);
      });
    });
    const catalogByCode = new Map(catalog.map((m) => [String(m.equipmentCode), m]));
    return [...byCode.entries()]
      .map(([equipmentCode, medicoes]) => {
        const m = catalogByCode.get(equipmentCode);
        const equipment = m?.equipment || equipmentCode;
        const brandNome = m?.brandNome || '';
        const subgroupNome = m?.subgroupNome || '';
        return {
          equipmentCode,
          equipment,
          brandNome,
          subgroupNome,
          medicoes: [...medicoes].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
          busca: `${equipment} ${brandNome} ${subgroupNome} ${equipmentCode}`.toLowerCase(),
        };
      })
      .sort((a, b) => a.equipment.localeCompare(b.equipment, 'pt-BR'));
  }, [formas, catalog, showInactive]);

  const formasFiltradas = useMemo(() => {
    const q = buscaForma.trim().toLowerCase();
    return formas.filter((f) => {
      if (!showInactive && f.isActive === false) return false;
      if (q && !f.nome.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [formas, buscaForma, showInactive]);

  useEffect(() => {
    const next = formasFiltradas[0] ? formasFiltradas[0].id : null;
    if (sel != null && formasFiltradas.some((f) => f.id === sel)) return;
    if (sel === next) return;
    setSel(next);
  }, [formasFiltradas, sel]);

  const itensBase = useMemo(() => {
    if (isModelos) {
      return catalog.map((m) => ({
        id: m.equipmentCode,
        rowKey: m.equipmentCode,
        nome: m.equipment,
        sub: m.brandNome,
        chave: agrup === 'fab' ? m.brandNome || 'Sem fabricante' : m.subgroupNome || 'Sem tecnologia',
        cor: techColor(m.subgroupNome),
        busca: `${m.equipment} ${m.subgroupNome} ${m.brandNome} ${m.equipmentCode}`.toLowerCase(),
        fabricante: m.brandNome,
      }));
    }
    return gruposHits.map((g) => ({
      id: g.idCollectionsGroup,
      rowKey: g.idCollectionsGroup,
      nome: g.nameCollectionsGroup || g.idCollectionsGroup,
      sub: g.idContract,
      chave: g.contractLabel,
      cor: '#7c6f9c',
      busca: `${g.nameCollectionsGroup} ${g.contractLabel} ${g.idCollectionsGroup}`.toLowerCase(),
      fabricante: '',
      meta: g,
    }));
  }, [isModelos, agrup, catalog, gruposHits]);

  const vincSet = useMemo(() => new Set(forma ? forma[campo] : []), [forma, campo]);

  const modelosEquipCount = useMemo(
    () => (forma ? countEquipForLinks(forma.modelos) : 0),
    [forma]
  );

  const usoOutras = useMemo(() => {
    const uso = {};
    formas.forEach((fr) => {
      if (!forma || fr.id === forma.id) return;
      fr[campo].forEach((id) => {
        if (!uso[id]) uso[id] = [];
        uso[id].push(fr.nome);
      });
    });
    return uso;
  }, [formas, forma, campo]);

  const itensVisiveis = useMemo(() => {
    if (!isModelos && !gruposBuscaAtiva) return [];
    const q = isModelos ? busca.trim().toLowerCase() : '';
    return itensBase.filter((it) => {
      if (isModelos && q && !it.busca.includes(q)) return false;
      if (isModelos && fabricante !== 'todos' && it.fabricante !== fabricante) return false;
      const on = vincSet.has(it.id);
      if (status === 'vinculados' && !on) return false;
      if (status === 'livres' && on) return false;
      return true;
    });
  }, [itensBase, busca, isModelos, fabricante, status, vincSet, gruposBuscaAtiva]);

  const gruposVisiveis = useMemo(() => groupBy(itensVisiveis, (it) => it.chave), [itensVisiveis]);

  const vinculadosAgrupados = useMemo(() => {
    if (!forma) return [];
    if (isModelos) {
      const linked = itensBase.filter((it) => vincSet.has(it.id));
      return groupBy(linked, (it) => it.chave);
    }
    const linked = (forma.gruposInfo || [])
      .map(mapGrpInfo)
      .filter(Boolean)
      .filter((g) => vincSet.has(g.idCollectionsGroup))
      .map((g) => ({
        id: g.idCollectionsGroup,
        rowKey: g.idCollectionsGroup,
        nome: g.nameCollectionsGroup || g.idCollectionsGroup,
        sub: g.idContract,
        chave: g.contractLabel,
        cor: '#7c6f9c',
      }));
    return groupBy(linked, (it) => it.chave);
  }, [forma, itensBase, isModelos, vincSet]);

  const patchForma = (fn) => {
    setFormas((prev) => prev.map((f) => (f.id === sel ? { ...f, ...fn(f) } : f)));
  };

  const persistModelos = async (meterId, nextKeys) => {
    setSavingLinks(true);
    try {
      const data = await putGoMeterTechLinks(meterId, codesToItems(nextKeys, catalog));
      const saved = Array.isArray(data?.modelos) ? data.modelos.map(String) : nextKeys;
      setFormas((prev) =>
        prev.map((f) => (f.id === meterId ? { ...f, modelos: saved } : f))
      );
    } catch (err) {
      toastError(err.message || 'Erro ao salvar vínculos');
      await loadFormas();
    } finally {
      setSavingLinks(false);
    }
  };

  const applyModelosChange = (nextKeys) => {
    if (!forma || savingLinks) return;
    const unique = [...new Set(nextKeys.map(String))];
    patchForma(() => ({ modelos: unique }));
    persistModelos(forma.id, unique);
  };

  const buildGruposInfo = (nextCodes, formaAtual) => {
    const map = new Map();
    (formaAtual?.gruposInfo || []).forEach((g) => {
      const info = mapGrpInfo(g);
      if (info) map.set(info.idCollectionsGroup, info);
    });
    gruposHits.forEach((g) => {
      const info = mapGrpInfo(g);
      if (info) map.set(info.idCollectionsGroup, info);
    });
    return nextCodes.map((code) => map.get(String(code))).filter(Boolean);
  };

  const persistGrupos = async (meterId, nextCodes, nextInfo) => {
    setSavingLinks(true);
    try {
      const data = await putGoMeterGrpLinks(meterId, gruposToLinkItems(nextCodes, nextInfo));
      const saved = Array.isArray(data?.grupos) ? data.grupos.map(String) : nextCodes;
      setFormas((prev) =>
        prev.map((f) =>
          f.id === meterId
            ? {
                ...f,
                grupos: saved,
                gruposInfo: nextInfo.filter((g) => saved.includes(g.idCollectionsGroup)),
              }
            : f
        )
      );
    } catch (err) {
      toastError(err.message || 'Erro ao salvar vínculos de grupos');
      await loadFormas();
    } finally {
      setSavingLinks(false);
    }
  };

  const applyGruposChange = (nextKeys) => {
    if (!forma || savingLinks) return;
    const unique = [...new Set(nextKeys.map(String))];
    const nextInfo = buildGruposInfo(unique, forma);
    const missingEquip = unique.filter((gid) => {
      const info = nextInfo.find((g) => g.idCollectionsGroup === gid);
      return !info || !info.equipmentCodes.length;
    });
    if (missingEquip.length) {
      toastError('Grupo sem equipamentos ativos para vincular.');
      return;
    }
    patchForma(() => ({ grupos: unique, gruposInfo: nextInfo }));
    persistGrupos(forma.id, unique, nextInfo);
  };

  const toggleItem = (id) => {
    if (!forma) return;
    if (isModelos) {
      const has = forma.modelos.includes(id);
      applyModelosChange(
        has ? forma.modelos.filter((x) => x !== id) : [...forma.modelos, id]
      );
      return;
    }
    const has = forma.grupos.includes(id);
    applyGruposChange(has ? forma.grupos.filter((x) => x !== id) : [...forma.grupos, id]);
  };

  const setListaVisivel = (add) => {
    if (!forma) return;
    const ids = [...new Set(itensVisiveis.map((it) => it.id))];
    if (isModelos) {
      let lista = forma.modelos.slice();
      if (add) ids.forEach((x) => { if (!lista.includes(x)) lista.push(x); });
      else lista = lista.filter((x) => !ids.includes(x));
      applyModelosChange(lista);
      return;
    }
    let lista = forma.grupos.slice();
    if (add) ids.forEach((x) => { if (!lista.includes(x)) lista.push(x); });
    else lista = lista.filter((x) => !ids.includes(x));
    applyGruposChange(lista);
  };

  const toggleGrupo = (_label, ids, todosOn) => {
    if (!forma) return;
    const uniq = [...new Set(ids)];
    if (isModelos) {
      let lista = forma.modelos.slice();
      if (todosOn) lista = lista.filter((x) => !uniq.includes(x));
      else uniq.forEach((x) => { if (!lista.includes(x)) lista.push(x); });
      applyModelosChange(lista);
      return;
    }
    let lista = forma.grupos.slice();
    if (todosOn) lista = lista.filter((x) => !uniq.includes(x));
    else uniq.forEach((x) => { if (!lista.includes(x)) lista.push(x); });
    applyGruposChange(lista);
  };

  const clearVinculos = () => {
    if (!forma) return;
    if (isModelos) {
      applyModelosChange([]);
      return;
    }
    applyGruposChange([]);
  };

  const openNova = () => {
    setModal({ id: null, nome: '', unit: '', medidor: true, erp: false, erpOps: [], isActive: true });
  };

  const openEditar = (f) => {
    setModal({
      id: f.id,
      nome: f.nome,
      unit: f.unit || '',
      medidor: f.medidor,
      erp: f.erp,
      erpOps: (f.erpOps || []).slice(0, 1),
      isActive: f.isActive !== false,
    });
  };

  const saveModal = async (draft) => {
    const nome = String(draft.nome || '').trim();
    if (!nome || savingModal) return;
    try {
      setSavingModal(true);
      const payload = {
        nome,
        unit: String(draft.unit || '').trim().slice(0, 3),
        requiresPrevMeter: Boolean(draft.medidor),
        erpEnabled: Boolean(draft.erp),
        erpOps: draft.erp ? draft.erpOps || [] : [],
      };
      if (draft.id) {
        const row = await updateGoMeter(draft.id, {
          ...payload,
          isActive: draft.isActive !== false,
        });
        setFormas((prev) =>
          prev.map((f) => (f.id === draft.id ? mapMeterToForma(row, f) : f))
        );
        toastSuccess(
          row.isActive === false ? 'Tipo de medição inativado' : 'Tipo de medição atualizado'
        );
      } else {
        const row = await createGoMeter(payload);
        const next = mapMeterToForma(row);
        setFormas((prev) => [...prev, next].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')));
        setSel(next.id);
        toastSuccess('Tipo de medição criado');
      }
      setModal(null);
    } catch (err) {
      toastError(err.message || 'Erro ao salvar tipo de medição');
    } finally {
      setSavingModal(false);
    }
  };

  const expandAll = Object.keys(Object.fromEntries(gruposVisiveis.map((g) => [g.label, true])));
  const allOpen =
    gruposVisiveis.length > 0 && gruposVisiveis.every((g) => Boolean(abertos[g.label]));

  const toggleExpandAll = () => {
    if (allOpen) setAbertos({});
    else {
      const next = {};
      expandAll.forEach((k) => {
        next[k] = true;
      });
      setAbertos(next);
    }
  };

  return (
    <div className="pcs2-page go-medicoes-page">
      <Pcs2Section
        className="go-medicoes-section"
        icon={IconGauge}
        title="Tipo de medição"
        desc="Cadastro tipos de medição Disponíveis no App Go e configure"
        headingExtra={
          <GoMedListSettings showInactive={showInactive} onChange={setShowInactive} />
        }
      >
        <div className="go-med-workspace">
          <aside className="go-med-col-formas" aria-label="Lista de tipos de medição">
            <div className="go-med-formas-head">
              <div className="go-med-formas-tools">
                <button
                  type="button"
                  className={[
                    'go-med-search-toggle',
                    'board-clickup-icon-btn-anchor',
                    buscaFormaOpen || buscaForma ? 'is-active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-label="Pesquisar tipos"
                  aria-expanded={buscaFormaOpen}
                  onClick={() => setBuscaFormaOpen((open) => !open)}
                >
                  <Search size={13} strokeWidth={2} aria-hidden />
                </button>
                <EcClickUpFilterChip
                  label="Nova"
                  icon={NovaFormaChipIcon}
                  showValue={false}
                  ariaLabel="Novo tipo de medição"
                  onClick={openNova}
                />
              </div>
              {buscaFormaOpen ? (
                <div className="go-med-search go-med-search--formas">
                  <IconSearch size={13} stroke={1.75} aria-hidden />
                  <input
                    ref={buscaFormaInputRef}
                    type="search"
                    value={buscaForma}
                    onChange={(e) => setBuscaForma(e.target.value)}
                    placeholder="Buscar tipo…"
                    aria-label="Buscar tipo de medição"
                  />
                </div>
              ) : null}
            </div>
            <div className="go-med-formas-list">
              {loading ? (
                <ProcessConfigSkeleton variant="listBody" ariaLabel="Carregando tipos de medição" />
              ) : loadError ? (
                <div className="go-med-empty dashed">
                  <strong>Não foi possível carregar</strong>
                  <span>{loadError}</span>
                  <button type="button" className="go-med-btn ghost compact" onClick={loadFormas}>
                    Tentar novamente
                  </button>
                </div>
              ) : formasFiltradas.length === 0 ? (
                <div className="go-med-empty dashed">
                  <strong>Nenhum tipo cadastrado</strong>
                  <span>Clique em Nova para criar o primeiro.</span>
                </div>
              ) : (
                formasFiltradas.map((f) => {
                  const active = f.id === sel;
                  return (
                    <div
                      key={f.id}
                      className={`go-med-forma-card${active ? ' is-active' : ''}${
                        f.isActive === false ? ' is-inactive' : ''
                      }`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSel(f.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSel(f.id);
                        }
                      }}
                    >
                      <div className="go-med-forma-row">
                        <span className={`go-med-dot${active ? ' is-on' : ''}`} />
                        <span className="go-med-forma-nome">{f.nome}</span>
                        <GoHoverTooltip text="Editar tipo de medição" align="center">
                          <button
                            type="button"
                            className="go-med-forma-edit-btn"
                            aria-label="Editar tipo de medição"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditar(f);
                            }}
                          >
                            <Pencil size={14} strokeWidth={2} aria-hidden />
                          </button>
                        </GoHoverTooltip>
                      </div>
                      <div className="go-med-forma-tags">
                        {f.unit ? (
                          <GoHoverTooltip text="Unidade de medida do medidor" align="center">
                            <span className="go-med-pill">{String(f.unit || '').toLowerCase()}</span>
                          </GoHoverTooltip>
                        ) : null}
                        {f.medidor ? (
                          <GoHoverTooltip text="Exige lançamento de medidor anterior no App" align="center">
                            <span className="go-med-pill">medidor anterior</span>
                          </GoHoverTooltip>
                        ) : null}
                        {f.erp ? (
                          <GoHoverTooltip text="Integrado com ERP para fechamento da OS" align="center">
                            <span className="go-med-pill">erp</span>
                          </GoHoverTooltip>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </aside>

          {forma ? (
            <section className="go-med-main" aria-label={forma.nome}>
              <header className="go-med-tabs">
                <button
                  type="button"
                  className={`go-med-tab${tab === 'modelos' ? ' is-active' : ''}`}
                  onClick={() => {
                    setTab('modelos');
                    setBusca('');
                    setStatus('todos');
                  }}
                >
                  Modelos de equipamento
                  <span className="go-med-tab-badge">{modelosEquipCount}</span>
                </button>
                <button
                  type="button"
                  className={`go-med-tab${tab === 'grupos' ? ' is-active' : ''}`}
                  onClick={() => {
                    setTab('grupos');
                    setBusca('');
                    setStatus('todos');
                  }}
                >
                  Grupos de cobrança
                  <span className="go-med-tab-badge">{forma.grupos.length}</span>
                </button>
                <div className="go-med-tabs-spacer" />
                <button
                  type="button"
                  className="go-med-btn ghost compact go-med-modelo-busca-btn"
                  onClick={() => setModeloBuscaOpen(true)}
                >
                  <Search size={13} strokeWidth={2} aria-hidden />
                  Buscar medições por modelo
                </button>
              </header>

              <div className="go-med-body">
                <Group orientation="horizontal" className="go-med-resize-group">
                  <Panel defaultSize="72%" minSize="48%" className="go-med-resize-panel">
                    <div className="go-med-selector">
                  <div className="go-med-toolbar">
                    <div className="go-med-search grow">
                      <IconSearch size={13} stroke={1.75} aria-hidden />
                      <input
                        type="search"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        placeholder={
                          isModelos
                            ? 'Buscar modelo…'
                            : 'Buscar contrato ou grupo de cobrança…'
                        }
                      />
                    </div>
                    {isModelos ? (
                      <BoardClickUpFilterChipDropdown
                        label="Fabricante"
                        icon={IconBuildingFactory2}
                        options={fabricantes}
                        selected={fabSelected}
                        isOpen={fabFilterOpen}
                        onOpenChange={(open) => {
                          setFabFilterOpen(open);
                          if (open) closeOtherFilters('fab');
                        }}
                        onToggleItem={(value) => {
                          setFabricante((cur) => (cur === value ? 'todos' : value));
                        }}
                        onClear={() => setFabricante('todos')}
                        showSearch={false}
                        countLabel="fabricantes"
                        listAriaLabel="Fabricantes"
                        emptyMessage="Nenhum fabricante"
                        pickerMode="simple"
                        pickerWidth={236}
                        pickerMaxHeight={320}
                        popoverClassName="board-search-filters-popover--tecnico ec-module-popover deq-filter-popover"
                      />
                    ) : null}
                    {(isModelos || gruposBuscaAtiva) ? (
                      <BoardClickUpFilterChipDropdown
                        label="Visão"
                        icon={IconEye}
                        options={['Vinculados', 'Livres']}
                        selected={visaoSelected}
                        isOpen={visaoFilterOpen}
                        onOpenChange={(open) => {
                          setVisaoFilterOpen(open);
                          if (open) closeOtherFilters('visao');
                        }}
                        onToggleItem={(value) => {
                          const next = VISAO_TO_STATUS[value] || 'todos';
                          setStatus((cur) => (cur === next ? 'todos' : next));
                        }}
                        onClear={() => setStatus('todos')}
                        showSearch={false}
                        countLabel="visões"
                        listAriaLabel="Visão"
                        emptyMessage="Nenhuma opção"
                        pickerMode="simple"
                        pickerWidth={236}
                        pickerMaxHeight={320}
                        popoverClassName="board-search-filters-popover--tecnico ec-module-popover deq-filter-popover"
                      />
                    ) : null}
                    {isModelos ? (
                      <BoardClickUpFilterChipDropdown
                        label="Agrupar"
                        icon={IconLayersSubtract}
                        options={['Tecnologia', 'Fabricante']}
                        selected={agrupSelected}
                        isOpen={agrupFilterOpen}
                        onOpenChange={(open) => {
                          setAgrupFilterOpen(open);
                          if (open) closeOtherFilters('agrup');
                        }}
                        onToggleItem={(value) => {
                          setAgrup(AGRUP_TO_KEY[value] || 'tec');
                          setAgrupFilterOpen(false);
                        }}
                        onClear={() => setAgrup('tec')}
                        showSearch={false}
                        countLabel="agrupamentos"
                        listAriaLabel="Agrupar"
                        emptyMessage="Nenhuma opção"
                        pickerMode="simple"
                        pickerWidth={236}
                        pickerMaxHeight={320}
                        popoverClassName="board-search-filters-popover--tecnico ec-module-popover deq-filter-popover"
                      />
                    ) : null}
                  </div>

                  {(isModelos || gruposBuscaAtiva) ? (
                    <div className="go-med-summary">
                      <span className="go-med-summary-meta">
                        {itensVisiveis.length} visíveis ·{' '}
                        {isModelos ? modelosEquipCount : forma.grupos.length} vinculados
                        {savingLinks ? ' · salvando…' : ''}
                        {gruposSearching ? ' · buscando…' : ''}
                      </span>
                      <div className="go-med-summary-actions">
                        <button
                          type="button"
                          className="go-med-link"
                          disabled={savingLinks || itensVisiveis.length === 0}
                          onClick={() => setListaVisivel(true)}
                        >
                          Vincular todos os visíveis
                        </button>
                        <button
                          type="button"
                          className="go-med-link muted"
                          disabled={savingLinks || itensVisiveis.length === 0}
                          onClick={() => setListaVisivel(false)}
                        >
                          Desvincular visíveis
                        </button>
                        {gruposVisiveis.length > 0 ? (
                          <>
                            <span className="go-med-dot-sep">·</span>
                            <button
                              type="button"
                              className="go-med-link"
                              onClick={toggleExpandAll}
                            >
                              {allOpen ? 'Recolher tudo' : 'Expandir tudo'}
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  <div className="go-med-groups">
                    {!isModelos && !gruposBuscaAtiva ? (
                      <div className="go-med-search-hint">
                        <div className="go-med-search-hint-inner">
                          <IconSearch size={40} stroke={1.25} className="go-med-search-hint-icon" aria-hidden />
                          <h3 className="go-med-search-hint-title">Busque um contrato ou grupo</h3>
                          <p className="go-med-search-hint-desc">
                            Digite o número do contrato ou o nome do grupo de cobrança na busca acima
                            para listar os resultados e marcar os vínculos.
                          </p>
                        </div>
                      </div>
                    ) : null}
                    {!isModelos && gruposBuscaAtiva && gruposSearching && gruposVisiveis.length === 0 ? (
                      <div className="go-med-empty">Buscando grupos…</div>
                    ) : null}
                    {!isModelos && gruposBuscaAtiva && gruposSearchError ? (
                      <div className="go-med-empty">{gruposSearchError}</div>
                    ) : null}
                    {gruposVisiveis.map((g) => {
                      const aberto = Boolean(abertos[g.label]);
                      const ids = g.list.map((it) => it.id);
                      const onCount = ids.filter((id) => vincSet.has(id)).length;
                      const todosOn = onCount === ids.length && ids.length > 0;
                      const cor = g.list[0]?.cor || '#94a3b8';
                      return (
                        <div key={g.label} className="go-med-group">
                          <div className="go-med-group-head">
                            <button
                              type="button"
                              className="go-med-group-toggle"
                              onClick={() =>
                                setAbertos((prev) => ({
                                  ...prev,
                                  [g.label]: !prev[g.label],
                                }))
                              }
                            >
                              <span className={`go-med-chevron${aberto ? ' is-open' : ''}`}>▸</span>
                              <span className="go-med-dot-sm" style={{ background: cor }} />
                              <span className="go-med-group-label">{g.label}</span>
                              <span className="go-med-mono">
                                {onCount}/{ids.length}
                              </span>
                            </button>
                            <button
                              type="button"
                              className="go-med-group-action"
                              disabled={savingLinks}
                              onClick={() => toggleGrupo(g.label, ids, todosOn)}
                            >
                              {todosOn ? 'Desvincular' : 'Vincular todos'}
                            </button>
                          </div>
                          {aberto ? (
                            <div className="go-med-group-body">
                              {g.list.map((it) => {
                                const on = vincSet.has(it.id);
                                const outros = usoOutras[it.id] || [];
                                return (
                                  <button
                                    key={it.rowKey}
                                    type="button"
                                    className={`go-med-item${on ? ' is-on' : ''}`}
                                    disabled={savingLinks}
                                    onClick={() => toggleItem(it.id)}
                                  >
                                    <span className={`go-med-check${on ? ' is-on' : ''}`} aria-hidden />
                                    <span className="go-med-item-nome">{it.nome}</span>
                                    <span className="go-med-item-sub">{it.sub}</span>
                                    <span className="go-med-item-spacer" />
                                    {outros.length ? (
                                      <GoHoverTooltip
                                        text={outros.join(', ')}
                                        align="end"
                                        wrap
                                      >
                                        <span className="go-med-tag soft">
                                          em {outros.length} outra{outros.length > 1 ? 's' : ''}
                                        </span>
                                      </GoHoverTooltip>
                                    ) : null}
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                    {(isModelos || gruposBuscaAtiva) &&
                    !gruposSearching &&
                    !gruposSearchError &&
                    gruposVisiveis.length === 0 ? (
                      <div className="go-med-empty">Nenhum item corresponde aos filtros.</div>
                    ) : null}
                  </div>
                    </div>
                  </Panel>

                  <Separator className="go-med-resize-handle" />

                  <Panel defaultSize="28%" minSize="18%" maxSize="42%" className="go-med-resize-panel">
                    <aside className="go-med-vinculos" aria-label="Vinculados">
                  <div className="go-med-vinculos-head">
                    <div>
                      <div className="go-med-vinculos-title">
                        {isModelos ? 'Modelos vinculados' : 'Grupos vinculados'}
                      </div>
                      <div className="go-med-mono">
                        {isModelos ? modelosEquipCount : forma.grupos.length}{' '}
                        {isModelos ? 'modelo(s)' : 'grupo(s)'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="go-med-link muted"
                      disabled={savingLinks}
                      onClick={clearVinculos}
                    >
                      Remover todos
                    </button>
                  </div>
                  <div className="go-med-vinculos-list">
                    {vinculadosAgrupados.map((v) => (
                      <div key={v.label} className="go-med-vinc-block">
                        <div className="go-med-vinc-label">
                          <span
                            className="go-med-dot-sm"
                            style={{ background: v.list[0]?.cor || '#94a3b8' }}
                          />
                          <span>{v.label}</span>
                          <span className="go-med-mono">{v.list.length}</span>
                        </div>
                        {v.list.map((vi) => (
                          <div key={vi.rowKey} className="go-med-vinc-row">
                            <span className="go-med-vinc-nome">{vi.nome}</span>
                            <span className="go-med-item-sub">{vi.sub}</span>
                            <GoHoverTooltip text="Desvincular" align="center">
                              <button
                                type="button"
                                className="go-med-icon-btn tiny"
                                aria-label="Desvincular"
                                disabled={savingLinks}
                                onClick={() => toggleItem(vi.id)}
                              >
                                <IconX size={11} stroke={1.75} />
                              </button>
                            </GoHoverTooltip>
                          </div>
                        ))}
                      </div>
                    ))}
                    {vinculadosAgrupados.length === 0 ? (
                      <div className="go-med-empty dashed">
                        <strong>Nenhum vínculo ainda</strong>
                        <span>
                          {isModelos
                            ? 'Selecione modelos na lista ao lado para vincular.'
                            : 'Selecione grupos de cobrança na lista ao lado.'}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </aside>
                  </Panel>
                </Group>
              </div>
            </section>
          ) : (
            <div className="go-med-empty-main">
              {loading ? (
                <ProcessConfigSkeleton variant="settings" embedded ariaLabel="Carregando medição" />
              ) : loadError ? (
                loadError
              ) : (
                'Selecione ou crie um tipo de medição.'
              )}
            </div>
          )}
        </div>
      </Pcs2Section>

      <BuscaModeloModal
        open={modeloBuscaOpen}
        models={modelosComMedicao}
        onClose={closeModeloBusca}
      />

      <MedicoesModal
        draft={modal}
        onClose={() => (!savingModal ? setModal(null) : null)}
        onChange={setModal}
        onSave={saveModal}
        submitting={savingModal}
      />
      {Toaster}
    </div>
  );
}
